import { AttachmentPreviewButton } from '@/shared/components/documents/AttachmentPreviewButton';
// One confirmed order, as the Dispatch team needs to work it.
//
// The queue answers "what is waiting". This answers "what do I pick, how is it
// packed, and is it here" — the line items with their packaging and grade, the
// stock actually on hand per line, and a place to record what went in the box.
//
// PACKED IS NOT SHIPPED. What is typed here is `packedQuantity`, Dispatch's own
// record of what they picked. `shippedQuantity` stays fulfilment's to write, so
// the two never contend for one number. Fulfil then moves what was packed —
// or the whole ordered quantity when nobody recorded anything.
//
// Stock is read from the inventory ledger, never CatalogProduct.currentStock,
// which no sale ever decrements.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, Package, PackageCheck, PackageX, AlertTriangle,
  MapPin, Phone, FileText, Truck, Check, RotateCcw, Trash2,
} from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { canMarkDispatch, canSetTransportAmount, canReverseDispatch } from '@/shared/utils/dispatchAccess';

import { LocalTransportModal, localTransportToForm, announceVehicleShare, type LocalTransport } from '../components/LocalTransportModal';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';

import { api } from '@/shared/api/axios';
import { cn } from '@/shared/utils/utils';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { formatINR } from '@/shared/utils/currency';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import { isCreditHandoff, needsLocalTransportDetails, needsPodForDelivery } from '../readiness';

interface DispatchLine {
  index: number;
  productId: string | null;
  description: string;
  /** Per-line customisation note from the quotation. */
  notes: string;
  productCode: string;
  grade: string;
  packagingType: string;
  unit: string;
  /** Pieces per container, and the combined pack-size text ("900 ML x 20"). */
  qtyPerPack: number;
  totalPackSize: string;
  required: number;
  /** null when the line has no catalog product, or that product has no balance row. */
  onHand: number | null;
  short: number;
  packedQuantity: number | null;
  shippedQuantity: number;
}

interface DispatchOrderDetail {
  _id: string;
  orderNumber: string;
  /** Export order — the only kind that has a third-party freight amount. */
  isExport?: boolean;
  status: string;
  date: string;
  confirmedAt: string | null;
  customerName: string;
  customerPhone: string;
  customerPoNumber: string;
  shippingDestination: string;
  shippingAddress?: { street?: string; city?: string; state?: string; zipCode?: string; country?: string };
  specialInstructions: string;
  /** The BDM's login, never their real name. */
  createdBy: string;
  invoices: { _id: string; number: string }[];
  /** Outstanding against this order, computed server-side (api-sales/orders.js
   *  `amountDue: invoiceDue`) and read by the payment summary below. */
  amountDue?: number;
  stockFlag: { status: string; promisedDate?: string; note?: string } | null;
  challans: {
    _id: string; documentNumber: string; status: string; date: string;
    dispatchedAt: string | null;
    vehicleNo: string; transporterName: string; driverName: string; driverPhone: string;
    lrNo: string; lrDate: string | null; ewayBillNo: string;
  }[];
  dispatchHandoff: {
    /** 'dispatched' once goods have left (the order's own status stays 'fulfilled'). */
    status?: string;
    dispatchedAt?: string | null;
    deliveredAt?: string | null;
    transportMode: string;
    driverName: string;
    courierName: string;
    courierDetails?: { address?: string; phone?: string; gstin?: string } | null;
    localTransport?: Record<string, unknown> | null;
    note: string;
    requiredDispatchDate: string | null;
    requiredDeliveryDate: string | null;
    paymentProofs?: { url: string; name: string }[];
    lrCopies?: { url: string; name: string }[];
    podCopies?: { url: string; name: string }[];
    creditCleared?: boolean;
    creditClearedAt?: string | null;
    creditClearanceNote?: string;
    creditClearedBy?: string | null;
    paymentArrangement?: string;
  } | null;
  creditCleared?: boolean;
  creditClearedAt?: string | null;
  creditClearanceNote?: string;
  creditClearedBy?: string | null;
  paymentArrangement?: string;
  lines: DispatchLine[];
}

function useDispatchOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['dispatch', 'order', id],
    queryFn: async () => {
      const { data } = await api.get<DispatchOrderDetail>(`/api/sales-ops/orders/${id}/dispatch-view`);
      return data;
    },
    enabled: !!id,
  });
}

function StockCell({ line }: { line: DispatchLine }) {
  if (line.onHand === null) {
    return (
      <span
        className="inline-flex items-center gap-1 text-body-xs font-medium text-on-surface-subtle"
        title="This line has no catalogue product, or that product has no stock record, so it cannot be checked automatically."
      >
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Stock unknown
      </span>
    );
  }
  if (line.short > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-body-xs font-medium text-danger-text">
        <PackageX className="h-3.5 w-3.5" aria-hidden /> <span className="font-mono tabular-nums">{line.onHand}</span> on hand · short <span className="font-mono tabular-nums">{line.short}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-body-xs font-medium text-success-text">
      <PackageCheck className="h-3.5 w-3.5" aria-hidden /> <span className="font-mono tabular-nums">{line.onHand}</span> on hand
    </span>
  );
}

export function DispatchOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useUser();
  const { data: order, isLoading, isError, error } = useDispatchOrder(id);

  // Dispatch team only, by department — shared/utils/dispatchAccess.ts.
  const canMarkDispatched = canMarkDispatch(user);
  // LOCAL TRANSPORT (client, 2026-10-05): Dispatch fills the details, an
  // Admin / Superadmin enters the amount, and only then can it be dispatched.
  // Local transport (client, 2026-10-06): Dispatch fills every detail; the
  // amount is Superadmin / Admin / Department Administrator's. Sales / BDMs
  // do not see it at all.
  const canEditLocalTransport = canMarkDispatched || canSetTransportAmount(user);

  // Keyed by line index. Held locally so a picker can fill the whole sheet in
  // and save once, rather than firing a write per keystroke.
  const [packed, setPacked] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!order) return;
    const seed: Record<number, string> = {};
    order.lines.forEach((l) => {
      seed[l.index] = l.packedQuantity === null ? '' : String(l.packedQuantity);
    });
    setPacked(seed);
  }, [order]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        packed: Object.entries(packed).map(([index, value]) => ({
          index: Number(index),
          packedQuantity: value.trim() === '' ? null : Number(value),
        })),
      };
      const { data } = await api.patch(`/api/sales-ops/orders/${id}/packed`, body);
      return data;
    },
    onSuccess: () => {
      toast.success('Packed quantities recorded.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not record what was packed.')),
  });

  // Mark as Dispatched first asks how the goods left the plant (mandatory).
  const [askLocalTransport, setAskLocalTransport] = useState(false);
  // Dispatch fills in / corrects it later (the amount or LR no. often comes after).
  const [editLocalTransport, setEditLocalTransport] = useState(false);
  const saveLocalTransport = useMutation({
    mutationFn: async (lt: LocalTransport) => (await api.put(`/api/sales-ops/orders/${id}/local-transport`, lt)).data,
    onSuccess: (data) => {
      setEditLocalTransport(false);
      toast.success('Local transport saved.');
      announceVehicleShare(data);
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not save the local transport.'),
  });
  const markDispatched = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/api/sales-ops/orders/${id}/mark-dispatched`, {});
      return res.data;
    },
    onSuccess: () => {
      setAskLocalTransport(false);
      toast.success('Order marked as dispatched! Stock movement posted.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to mark as dispatched.');
    },
  });

  const canClearCredit = ['admin', 'superadmin', 'department_admin', 'manager', 'accounts', 'accountant'].includes(String(user?.role || '').toLowerCase())
    || ['accounts', 'management'].includes(String(user?.department || '').toLowerCase());

  const clearCreditMutation = useMutation({
    mutationFn: async (note?: string) =>
      (await api.post(`/api/sales-ops/orders/${id}/clear-credit-dispatch`, { note })).data,
    onSuccess: () => {
      toast.success('Order cleared for dispatch under credit.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to clear credit for dispatch.'),
  });

  const revokeCreditClearanceMutation = useMutation({
    mutationFn: async () =>
      (await api.delete(`/api/sales-ops/orders/${id}/clear-credit-dispatch`)).data,
    onSuccess: () => {
      toast.success('Credit clearance revoked.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to revoke credit clearance.'),
  });

  // Mark Dispatched (client, 2026-10-07) — rules in ../readiness.ts, the same
  // as the server's (src/shared/dispatch/local-transport.js). Local transport
  // details only when the order uses local transport; no POD needed to
  // dispatch (it is needed to mark delivered).
  const startMarkDispatched = () => {
    if (needsLocalTransportDetails(order?.dispatchHandoff as Parameters<typeof needsLocalTransportDetails>[0])) {
      toast('Fill in the local transport first — transporter name, mobile and vehicle number.');
      setEditLocalTransport(true);
      return;
    }
    // The amount is NOT needed to dispatch (client, 2026-10-06): Superadmin /
    // Admin / Department Administrator enter it afterwards and are notified.
    setAskLocalTransport(true);
  };

  const markDelivered = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/api/sales-ops/orders/${id}/mark-delivered`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Order marked as delivered!');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to mark as delivered.');
    },
  });

  const reverseDispatch = useMutation({
    mutationFn: async () => (await api.post(`/api/sales-ops/orders/${id}/reverse-dispatch`)).data,
    onSuccess: () => {
      toast.success('Dispatch status reversed! Inventory stock restored.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not reverse dispatch.'),
  });

  const deleteDoc = useMutation({
    mutationFn: async ({ url, name, kind }: { url: string; name?: string; kind?: string }) => {
      return (await api.delete(`/api/sales-ops/orders/${id}/dispatch-document`, { data: { url, name, kind } })).data;
    },
    onSuccess: () => {
      toast.success('Document deleted. You can now upload a replacement.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to delete document.'),
  });

  const [uploadingPayment, setUploadingPayment] = useState(false);
  const addPaymentProofs = useMutation({
    mutationFn: async (files: { url: string; name: string }[]) => {
      for (const f of files) {
        await api.post(`/api/sales-ops/orders/${id}/dispatch-document`, {
          kind: 'payment_proof',
          url: f.url,
          name: f.name,
        });
      }
    },
    onSuccess: () => {
      toast.success('Payment proof uploaded.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to upload payment proof.'),
  });


  // The LR (Lorry Receipt) copy — Sales no longer asks for it at hand-off
  // time (the courier often only issues it after actually collecting the
  // goods), so Dispatch attaches it here once they genuinely have it.
  const [uploadingLr, setUploadingLr] = useState(false);
  const addLrCopies = useMutation({
    mutationFn: async (files: { url: string; name: string }[]) => api.patch(`/api/sales-ops/orders/${id}/lr-copies`, { lrCopies: files }),
    onSuccess: () => {
      toast.success('LR copy added.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not add the LR copy.')),
  });

  // POD — compulsory before Mark Delivered (client, 2026-10-07); the LR copy
  // above stays optional.
  const [uploadingPod, setUploadingPod] = useState(false);
  const addPodCopies = useMutation({
    mutationFn: async (files: { url: string; name: string }[]) => api.patch(`/api/sales-ops/orders/${id}/pod-copies`, { podCopies: files }),
    onSuccess: () => {
      toast.success('POD added.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not add the POD.')),
  });
  const uploadChosen = async (chosen: File[], setBusy: (b: boolean) => void, done: (files: { url: string; name: string }[]) => void) => {
    if (chosen.length === 0) return;
    setBusy(true);
    try {
      const added: { url: string; name: string }[] = [];
      for (const file of chosen) {
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`"${file.name}" is larger than 50 MB.`);
          continue;
        }
        const body = new FormData();
        body.append('file', file);
        const { data } = await api.post<{ url: string }>('/api/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) added.push({ url: data.url, name: file.name });
      }
      if (added.length) done(added);
    } catch {
      toast.error('The upload failed. The file was not attached.');
    } finally {
      setBusy(false);
    }
  };

  const dirty = useMemo(() => {
    if (!order) return false;
    return order.lines.some((l) => {
      const stored = l.packedQuantity === null ? '' : String(l.packedQuantity);
      return (packed[l.index] ?? '') !== stored;
    });
  }, [order, packed]);

  const totals = useMemo(() => {
    if (!order) return { ordered: 0, packedQty: 0, shortLines: 0 };
    return {
      ordered: order.lines.reduce((s, l) => s + l.required, 0),
      packedQty: order.lines.reduce((s, l) => s + (Number(packed[l.index]) || 0), 0),
      shortLines: order.lines.filter((l) => l.short > 0).length,
    };
  }, [order, packed]);

  // The most recently raised challan's OWN transport details — set later, by
  // Dispatch itself, and more authoritative than Sales' one-time hand-off
  // snapshot below (challans are returned oldest-first, so the last one is
  // the current shipment's record).
  const primaryChallan = order?.challans?.[order.challans.length - 1];
  // Where the order actually is. Marking it dispatched sets the order's status
  // to 'fulfilled' and records the dispatch on the hand-off and the challan —
  // the status alone never reads 'dispatched', so checking only that kept
  // "Mark as Dispatched" on screen after the goods had left (and hid "Mark as
  // Delivered" for good).
  const challanStatuses = (order?.challans || []).map((c) => c.status);
  const isDelivered = order?.status === 'delivered'
    || Boolean(order?.dispatchHandoff?.deliveredAt)
    || challanStatuses.includes('delivered');
  const isDispatched = isDelivered
    || order?.status === 'dispatched'
    || order?.dispatchHandoff?.status === 'dispatched'
    || Boolean(order?.dispatchHandoff?.dispatchedAt)
    || challanStatuses.includes('dispatched');
  const shippingAddressLine = order?.shippingAddress
    ? [order.shippingAddress.street, order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.zipCode]
        .filter(Boolean).join(', ')
    : '';

  // Loading fills the regions the real screen will occupy, so the sheet does
  // not jump when the order arrives.
  if (isLoading) {
    return (
      <div className="flex w-full flex-1 flex-col gap-5 p-4 md:p-6" aria-busy="true" aria-live="polite">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-5 w-56" />
          </div>
          <Skeleton className="h-9 w-40 rounded-lg" />
        </div>
        <Skeleton className="h-24 w-full rounded-card" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-card" />)}
        </div>
        <Skeleton className="h-72 w-full rounded-card" />
      </div>
    );
  }
  if (isError || !order) {
    // A 403 is not an empty result: say who can open it instead of implying
    // the order does not exist.
    const forbidden = (error as { response?: { status?: number } } | null)?.response?.status === 403;
    return (
      <div className="p-6">
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title={forbidden ? 'You cannot open this order' : 'Order not found'}
          description={forbidden
            ? 'The picking sheet is for the Dispatch team and Accounts. Ask Dispatch if you need this order worked.'
            : 'It may have been cancelled, or the number is wrong. Nothing was lost — check the queue for the order you meant.'}
          action={<Button onClick={() => navigate(ROUTES.dispatchOrders)}>Back to Dispatch Orders</Button>}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-5 p-4 md:p-6">
      {editLocalTransport && (
        <LocalTransportModal
          mode="edit"
          orderNumber={order.orderNumber}
          sentVia={order.dispatchHandoff?.transportMode === 'third_party_courier' ? order.dispatchHandoff?.courierName : undefined}
          thirdParty={order.dispatchHandoff?.transportMode === 'third_party_courier'}
          twoLegs={order.dispatchHandoff?.transportMode === 'third_party_courier'}
          localOnly={order.dispatchHandoff?.transportMode === 'local_transport' || (order.dispatchHandoff as { localTransportUsed?: boolean | null } | undefined)?.localTransportUsed === true}
          initial={localTransportToForm(order.dispatchHandoff?.localTransport as Record<string, unknown> | null, order.dispatchHandoff as unknown as Record<string, unknown>)}
          busy={saveLocalTransport.isPending}
          onCancel={() => setEditLocalTransport(false)}
          onConfirm={(lt) => saveLocalTransport.mutate(lt)}
        />
      )}
      {/* Dispatch's job is the LR and the status — the local transport was
          recorded by Sales at hand-off — so this is only a confirmation. */}
      <ConfirmDialog
        open={askLocalTransport}
        title={`Mark ${order.orderNumber || 'order'} as dispatched?`}
        message="The goods have left the plant. Stock is moved out and the sales person is told."
        confirmLabel="Mark as Dispatched"
        loading={markDispatched.isPending}
        onConfirm={() => markDispatched.mutate()}
        onCancel={() => setAskLocalTransport(false)}
      />
      {/* ── Document identity ─────────────────────────────────────────────────
          The order number is what the challan and the LR carry, so it leads in
          mono; the customer is the headline the picker reads. */}
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-4">
        <div className="flex items-start gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(ROUTES.dispatchOrders)}
            aria-label="Back to Dispatch Orders"
            className="h-9 w-9 rounded-lg"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-card-sm border border-border-strong bg-surface-variant px-2 py-0.5 font-mono text-data-xs font-semibold tracking-tight text-on-surface">
                {order.orderNumber || order._id.slice(-6)}
              </span>
              <Badge tone="neutral">{order.status}</Badge>
              {order.customerPoNumber && (
                <span className="text-body-xs text-on-surface-variant">Customer PO <span className="font-mono tabular-nums text-on-surface">{order.customerPoNumber}</span></span>
              )}
            </div>
            <h1 className="mt-1 break-words font-display text-display-sm font-semibold text-on-surface">{order.customerName || '—'}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-on-surface-variant">
              {order.customerPhone && (
                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" aria-hidden /> <span className="font-mono tabular-nums">{order.customerPhone}</span></span>
              )}
              {(shippingAddressLine || order.shippingDestination) && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" aria-hidden /> {shippingAddressLine || order.shippingDestination}</span>
              )}
              <span>Confirmed <span className="font-mono tabular-nums text-on-surface">{order.confirmedAt ? new Date(order.confirmedAt).toLocaleDateString('en-IN') : order.date ? new Date(order.date).toLocaleDateString('en-IN') : '—'}</span></span>
              {order.createdBy && <span>Raised by <span className="font-medium uppercase text-on-surface-variant">{order.createdBy}</span></span>}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {!isDispatched && (() => {
            const isCreditOrder = isCreditHandoff(order.dispatchHandoff as Parameters<typeof isCreditHandoff>[0])
              || String(order.paymentArrangement || order.dispatchHandoff?.paymentArrangement || '').toLowerCase().startsWith('credit');
            const isCreditCleared = Boolean(order.creditCleared || order.dispatchHandoff?.creditCleared);
            if (isCreditCleared) {
              return (
                <div className="flex items-center gap-1.5">
                  <span
                    title={order.creditClearanceNote ? `Cleared on credit: ${order.creditClearanceNote}` : 'Cleared for dispatch under credit'}
                    className="inline-flex items-center gap-1 rounded-lg border border-success/30 bg-success/10 px-2.5 py-1.5 text-label-sm font-semibold text-success-text"
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden /> Credit cleared
                  </span>
                  {canClearCredit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Revoke credit clearance for this order?')) {
                          revokeCreditClearanceMutation.mutate();
                        }
                      }}
                      disabled={revokeCreditClearanceMutation.isPending}
                      className="h-8 border-destructive/30 px-2 text-body-sm text-destructive hover:bg-destructive-subtle"
                      title="Revoke credit clearance"
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              );
            }
            if (canClearCredit && (isCreditOrder || (order.amountDue ?? 0) > 0)) {
              return (
                <Button
                  onClick={() => {
                    const note = window.prompt(`Clear order ${order.orderNumber} for dispatch under credit? Enter approval note:`, 'Approved on credit');
                    if (note !== null) clearCreditMutation.mutate(note);
                  }}
                  disabled={clearCreditMutation.isPending}
                  className="h-9 cursor-pointer text-body-sm font-semibold"
                  title="Approve order for dispatch under credit"
                >
                  Clear on Credit
                </Button>
              );
            }
            if (isCreditOrder && (order.amountDue ?? 0) > 0) {
              return (
                <span className="inline-flex items-center gap-1 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-1.5 text-label-sm font-semibold text-warning-text">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Credit clearance required
                </span>
              );
            }
            return null;
          })()}
          {canMarkDispatched && !isDispatched && (
            <Button
              onClick={startMarkDispatched}
              disabled={markDispatched.isPending}
              className="cursor-pointer font-semibold"
            >
              <Truck className="h-4 w-4" />
              <span>Mark as Dispatched</span>
            </Button>
          )}
          {isDispatched && !isDelivered && (
            <div className="flex flex-col items-end">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-info/30 bg-info/10 px-3 py-1.5 text-label-sm font-semibold text-info-text">
                <Truck className="h-4 w-4" aria-hidden /> Dispatched
              </span>
              {(order.dispatchHandoff?.dispatchedAt || primaryChallan?.dispatchedAt) && (
                <span className="mt-0.5 text-body-xs text-on-surface-subtle">
                  on <span className="font-mono tabular-nums">{new Date(order.dispatchHandoff?.dispatchedAt || primaryChallan?.dispatchedAt!).toLocaleString('en-IN')}</span>
                </span>
              )}
            </div>
          )}
          {canMarkDispatched && isDispatched && !isDelivered && (
            <Button
              onClick={() => markDelivered.mutate()}
              disabled={markDelivered.isPending}
              variant="outline"
              className="cursor-pointer font-semibold"
            >
              <Check className="h-4 w-4" />
              <span>Mark as Delivered</span>
            </Button>
          )}
          {isDelivered && (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-success/30 bg-success/10 px-3 py-1.5 text-label-sm font-semibold text-success-text">
              <Check className="h-4 w-4" aria-hidden /> Delivered
            </span>
          )}
          {canReverseDispatch(user) && (isDispatched || isDelivered) && (
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm(`Are you sure you want to REVERSE dispatch for order ${order.orderNumber || ''}? This requires Admin authorization and will restore inventory stock.`)) {
                  reverseDispatch.mutate();
                }
              }}
              disabled={reverseDispatch.isPending}
              className="cursor-pointer border-destructive/30 font-semibold text-destructive hover:bg-destructive-subtle"
              title="Reverse dispatch (Admin only)"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reverse Dispatch</span>
            </Button>
          )}
        </div>
      </header>


      {order.stockFlag && (
        <div className="rounded-card border border-warning/30 bg-warning/10 p-4 text-body-md">
          <p className="flex items-center gap-1.5 text-label-md font-semibold text-warning-text">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Stock shortage flagged{order.stockFlag.status === 'promised' ? ' — ship date promised' : ''}
          </p>
          {order.stockFlag.promisedDate && (
            <p className="mt-1 text-body-sm text-warning-text">Promised <span className="font-mono tabular-nums">{new Date(order.stockFlag.promisedDate).toLocaleDateString('en-IN')}</span></p>
          )}
          {order.stockFlag.note && <p className="mt-0.5 text-body-sm text-on-surface-variant">{order.stockFlag.note}</p>}
        </div>
      )}

      <section className="rounded-card border border-border bg-card shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <h2 className="text-headline-sm font-semibold text-on-surface">Transport and hand-off</h2>
          <span className="text-body-xs text-on-surface-subtle">Recorded by Sales at hand-off · amounts entered by Admin</span>
        </div>
        <div className="grid gap-x-6 gap-y-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Transport">
            {order.dispatchHandoff?.transportMode === 'local_transport'
              ? 'Local transport (details by Dispatch)'
              : order.dispatchHandoff?.transportMode === 'in_house_driver'
              ? `Driver — ${primaryChallan?.driverName || order.dispatchHandoff.driverName || '—'}`
              : `Courier — ${primaryChallan?.transporterName || order.dispatchHandoff?.courierName || '—'}`}
          </Field>
          {primaryChallan?.vehicleNo && (
            <Field label="Vehicle"><span className="font-mono uppercase">{primaryChallan.vehicleNo}</span></Field>
          )}
          {order.dispatchHandoff?.courierDetails?.gstin && (
            <Field label="Transporter GSTIN"><span className="font-mono">{order.dispatchHandoff.courierDetails.gstin}</span></Field>
          )}
          {(() => {
            const h = order.dispatchHandoff as unknown as { localTransportUsed?: boolean | null; freight?: { amount?: number | null; payment?: string } | null; transportMode?: string } | undefined;
            const fr = h?.freight;
            const frText = fr && fr.amount !== null && fr.amount !== undefined ? formatINR(fr.amount) : '';
            return (
              <>
                {/* Sales enters it at hand-off; never shown to the Dispatch team (2026-10-06). */}
                {h?.transportMode === 'third_party_courier' && (frText || canSetTransportAmount(user)) && (
                  <Field label="Third-party transporter amount" wide>
                    {frText ? (
                      <>
                        <span className="font-mono tabular-nums font-semibold text-on-surface">{frText}</span>
                        {fr?.payment === 'to_pay' && <Badge tone="warning" className="ml-2">To pay</Badge>}
                        {fr?.payment === 'paid' && <Badge tone="success" className="ml-2">Paid</Badge>}
                      </>
                    ) : <MissingChip>Amount not entered</MissingChip>}
                    {canSetTransportAmount(user) && <InlineAction onClick={() => setEditLocalTransport(true)}>{frText ? 'Edit' : 'Add'}</InlineAction>}
                  </Field>
                )}
                {canEditLocalTransport && h?.localTransportUsed === false && (
                  <Field label="Local transport" wide>
                    Not used — the third party picks up directly
                    <InlineAction onClick={() => setEditLocalTransport(true)}>Change</InlineAction>
                  </Field>
                )}
              </>
            );
          })()}
          {!canEditLocalTransport || (order.dispatchHandoff as unknown as { localTransportUsed?: boolean | null } | undefined)?.localTransportUsed === false ? null : order.dispatchHandoff?.localTransport?.name ? (() => {
            const lt = order.dispatchHandoff.localTransport as Record<string, unknown>;
            const amount = lt.amount !== undefined && lt.amount !== null && lt.amount !== '' ? formatINR(Number(lt.amount)) : '';
            return (
              <Field label="Local transport (plant → godown)" wide>
                <span className="font-semibold text-on-surface">{String(lt.name)}</span>
                <span className="mt-0.5 block text-body-sm text-on-surface-variant">
                  {[lt.mobile || lt.driverPhone, lt.vehicleNo, lt.trackingNo && `Tracking ${lt.trackingNo}`, lt.note]
                    .filter((x) => String(x ?? '').trim() !== '')
                    .map((x) => String(x)).join(' · ') || '—'}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-2">
                  {amount ? (
                    <>
                      <span className="font-mono tabular-nums font-semibold text-on-surface">{amount}</span>
                      {lt.payment === 'to_pay' && <Badge tone="warning">To pay</Badge>}
                      {lt.payment === 'paid' && <Badge tone="success">Paid</Badge>}
                      {Boolean(lt.paymentMode) && <span className="text-body-xs text-on-surface-subtle">{String(lt.paymentMode)}</span>}
                    </>
                  ) : (
                    lt.amountEntered !== true && <MissingChip>Amount pending — Admin</MissingChip>
                  )}
                  {canSetTransportAmount(user) && (
                    <InlineAction onClick={() => setEditLocalTransport(true)}>{amount ? 'Change amount' : 'Enter amount'}</InlineAction>
                  )}
                  {canEditLocalTransport && !canSetTransportAmount(user) && (
                    <InlineAction onClick={() => setEditLocalTransport(true)}>Edit</InlineAction>
                  )}
                </span>
              </Field>
            );
          })() : (canEditLocalTransport && (
            <Field label="Local transport (plant → godown)" wide>
              <MissingChip>Not recorded</MissingChip>
              <InlineAction onClick={() => setEditLocalTransport(true)}>Add</InlineAction>
            </Field>
          ))}
          <Field label="Dispatch by">
            {order.dispatchHandoff?.requiredDispatchDate ? <span className="font-mono tabular-nums">{new Date(order.dispatchHandoff.requiredDispatchDate).toLocaleDateString('en-IN')}</span> : <span className="text-on-surface-subtle">Not specified</span>}
          </Field>
          <Field label="Deliver by">
            {order.dispatchHandoff?.requiredDeliveryDate ? <span className="font-mono tabular-nums">{new Date(order.dispatchHandoff.requiredDeliveryDate).toLocaleDateString('en-IN')}</span> : <span className="text-on-surface-subtle">Not specified</span>}
          </Field>
          {primaryChallan?.dispatchedAt && (
            <Field label="Dispatched on"><span className="font-mono tabular-nums">{new Date(primaryChallan.dispatchedAt).toLocaleDateString('en-IN')}</span></Field>
          )}
          {primaryChallan?.lrNo && (
            <Field label="LR No">
              <span className="font-mono">{primaryChallan.lrNo}</span>
              {primaryChallan.lrDate && <span className="text-body-sm text-on-surface-subtle"> ({new Date(primaryChallan.lrDate).toLocaleDateString('en-IN')})</span>}
            </Field>
          )}
          {primaryChallan?.ewayBillNo && (
            <Field label="e-Way Bill"><span className="font-mono">{primaryChallan.ewayBillNo}</span></Field>
          )}
          {order.dispatchHandoff?.note && <Field label="Hand-off note" wide>{order.dispatchHandoff.note}</Field>}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-border px-4 py-3 text-body-sm">
          <span className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Payment proof</span>
          {order.dispatchHandoff?.paymentProofs?.length
            ? <Badge tone="success">{order.dispatchHandoff.paymentProofs.length} attached</Badge>
            : <Badge tone="neutral">Not attached</Badge>}
          {(order.dispatchHandoff?.paymentProofs || []).map((proof, index) => (
            <div key={proof.url || index} className="inline-flex items-center gap-1 rounded-card-sm border border-border bg-surface-variant px-1.5 py-0.5">
              <AttachmentPreviewButton url={proof.url} name={proof.name || `Payment proof ${index + 1}`} />
              <button
                type="button"
                disabled={deleteDoc.isPending}
                onClick={() => {
                  if (window.confirm(`Delete payment proof "${proof.name || 'document'}"? You can then upload a replacement.`)) {
                    deleteDoc.mutate({ url: proof.url, name: proof.name, kind: 'payment_proof' });
                  }
                }}
                className="cursor-pointer rounded p-0.5 text-on-surface-subtle transition hover:bg-destructive-subtle hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Delete payment proof to replace it"
                aria-label="Delete payment proof"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {!order.dispatchHandoff?.paymentProofs?.length && <span className="text-on-surface-subtle">Sales attaches it at hand-off.</span>}
          <input
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,application/pdf"
            disabled={uploadingPayment}
            aria-label="Upload payment proof"
            onChange={(e) => {
              const chosen = Array.from(e.target.files || []);
              e.target.value = '';
              void uploadChosen(chosen, setUploadingPayment, (added) => addPaymentProofs.mutate(added));
            }}
            className="text-body-xs text-on-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring file:mr-2 file:cursor-pointer file:rounded-lg file:border file:border-input file:bg-surface file:px-2 file:py-1 file:text-body-xs file:font-semibold file:text-on-surface-variant"
          />
          {uploadingPayment && <span className="text-on-surface-subtle">Uploading…</span>}
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-border px-4 py-3 text-body-sm">
          <span className="text-label-sm uppercase tracking-wider text-on-surface-subtle">LR copy</span>
          {order.dispatchHandoff?.lrCopies?.length
            ? <Badge tone="success">{order.dispatchHandoff.lrCopies.length} attached</Badge>
            : <Badge tone="warning">Missing</Badge>}
          {(order.dispatchHandoff?.lrCopies || []).map((doc, index) => (
            <div key={doc.url || index} className="inline-flex items-center gap-1 rounded-card-sm border border-border bg-surface-variant px-1.5 py-0.5">
              <AttachmentPreviewButton url={doc.url} name={doc.name || `LR copy ${index + 1}`} />
              <button
                type="button"
                disabled={deleteDoc.isPending}
                onClick={() => {
                  if (window.confirm(`Delete LR copy "${doc.name || 'document'}"? You can then upload a replacement.`)) {
                    deleteDoc.mutate({ url: doc.url, name: doc.name, kind: 'lr' });
                  }
                }}
                className="cursor-pointer rounded p-0.5 text-on-surface-subtle transition hover:bg-destructive-subtle hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Delete LR copy to replace it"
                aria-label="Delete LR copy"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {!order.dispatchHandoff?.lrCopies?.length && <span className="text-on-surface-subtle">Attach it once the courier issues it.</span>}
          <input
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,application/pdf"
            disabled={uploadingLr}
            aria-label="Upload LR copy"
            onChange={async (e) => {
              const chosen = Array.from(e.target.files || []);
              e.target.value = '';
              if (chosen.length === 0) return;
              setUploadingLr(true);
              try {
                const added: { url: string; name: string }[] = [];
                for (const file of chosen) {
                  if (file.size > 50 * 1024 * 1024) {
                    toast.error(`"${file.name}" is larger than 50 MB.`);
                    continue;
                  }
                  const body = new FormData();
                  body.append('file', file);
                  const { data } = await api.post<{ url: string }>('/api/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
                  if (data?.url) added.push({ url: data.url, name: file.name });
                }
                if (added.length) addLrCopies.mutate(added);
              } catch {
                toast.error('The upload failed. The file was not attached.');
              } finally {
                setUploadingLr(false);
              }
            }}
            className="text-body-xs text-on-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring file:mr-2 file:cursor-pointer file:rounded-lg file:border file:border-input file:bg-surface file:px-2 file:py-1 file:text-body-xs file:font-semibold file:text-on-surface-variant"
          />
          {uploadingLr && <span className="text-on-surface-subtle">Uploading…</span>}
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-border px-4 py-3 text-body-sm">
          <span className="text-label-sm uppercase tracking-wider text-on-surface-subtle">POD{!isCreditHandoff(order.dispatchHandoff) && <span className="text-destructive"> *</span>}</span>
          {order.dispatchHandoff?.podCopies?.length
            ? <Badge tone="success">{order.dispatchHandoff.podCopies.length} attached</Badge>
            : <Badge tone={needsPodForDelivery(order.dispatchHandoff as Parameters<typeof needsPodForDelivery>[0]) ? 'danger' : 'neutral'}>Missing</Badge>}
          {(order.dispatchHandoff?.podCopies || []).map((doc, index) => (
            <div key={doc.url || index} className="inline-flex items-center gap-1 rounded-card-sm border border-border bg-surface-variant px-1.5 py-0.5">
              <AttachmentPreviewButton url={doc.url} name={doc.name || `POD ${index + 1}`} />
              <button
                type="button"
                disabled={deleteDoc.isPending}
                onClick={() => {
                  if (window.confirm(`Delete POD "${doc.name || 'document'}"? You can then upload a replacement.`)) {
                    deleteDoc.mutate({ url: doc.url, name: doc.name, kind: 'pod' });
                  }
                }}
                className="cursor-pointer rounded p-0.5 text-on-surface-subtle transition hover:bg-destructive-subtle hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Delete POD to replace it"
                aria-label="Delete POD"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {needsPodForDelivery(order.dispatchHandoff as Parameters<typeof needsPodForDelivery>[0]) && <span className="text-body-sm font-medium text-danger-text">Upload it once delivered — required before Mark Delivered.</span>}
          <input
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,application/pdf"
            disabled={uploadingPod}
            aria-label="Upload POD"
            onChange={(e) => {
              const chosen = Array.from(e.target.files || []);
              e.target.value = '';
              void uploadChosen(chosen, setUploadingPod, (added) => addPodCopies.mutate(added));
            }}
            className="text-body-xs text-on-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring file:mr-2 file:cursor-pointer file:rounded-lg file:border file:border-input file:bg-surface file:px-2 file:py-1 file:text-body-xs file:font-semibold file:text-on-surface-variant"
          />
          {uploadingPod && <span className="text-on-surface-subtle">Uploading…</span>}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border px-4 py-3 text-body-sm">
          <span className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Delivery challan</span>
          {order.challans.filter((challan) => !challan.documentNumber.startsWith('DRAFT-')).map((challan) => (
            <a key={challan._id} href={`/api/documents/challan/${challan._id}`} target="_blank" rel="noreferrer" className="font-mono font-medium text-primary underline-offset-2 hover:text-primary-dim hover:underline">
              {challan.documentNumber}
            </a>
          ))}
          {!order.challans.some((challan) => !challan.documentNumber.startsWith('DRAFT-')) && <span className="text-on-surface-subtle">Not raised yet.</span>}
        </div>
      </section>

      {/* Already handed off? Without this the sheet looked identical whether a
          challan had been raised or not, and the only way to find out was to
          press the button again. */}
      {order.challans.length > 0 && (
        <div className="rounded-card border border-border bg-surface-variant px-4 py-3 text-body-sm">
          <p className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Handed off to Dispatch</p>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {order.challans.map((c) => (
              <button
                key={c._id}
                type="button"
                onClick={() => navigate(ROUTES.dispatchChallans)}
                className="font-mono text-body-sm font-medium text-on-surface underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {c.documentNumber.startsWith('DRAFT-') ? 'Dispatch record' : c.documentNumber}
                <span className="ml-1 font-sans text-body-xs capitalize text-on-surface-subtle">
                  {c.status.replaceAll('_', ' ')}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-body-xs text-on-surface-subtle">
            Stock is deducted when the challan is dispatched, not when it is created.
          </p>
        </div>
      )}

      {/* ── At a glance ────────────────────────────────────────────────────── */}
      <StatGrid cols={3}>
        <StatCard label="Lines to pick" value={order.lines.length} hint={`${totals.ordered} units ordered`} icon={Package} tone="neutral" />
        <StatCard label="Packed so far" value={totals.packedQty} hint={`of ${totals.ordered} units`} icon={PackageCheck} tone="primary" />
        <StatCard label="Short lines" value={totals.shortLines} hint={totals.shortLines ? 'Not enough stock on these lines' : 'All lines covered by stock on hand'} icon={PackageX} tone={totals.shortLines ? 'danger' : 'success'} />
      </StatGrid>

      {/* ── The picking sheet ──────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-headline-sm font-semibold text-on-surface">
              <Package className="h-4 w-4 text-primary" aria-hidden /> Products to pick
            </h2>
            <p className="mt-0.5 text-body-sm text-on-surface-variant">
              Enter what actually went in the box. Leave a cell blank if the line has not been picked yet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save packed quantities'}
            </Button>
            {canMarkDispatched && !isDispatched && (
              <Button
                onClick={startMarkDispatched}
                disabled={markDispatched.isPending}
                variant="outline"
                className="font-semibold"
              >
                <Truck className="h-4 w-4" />
                <span>Mark Dispatched</span>
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-body-md">
            <thead>
              <tr className="border-b border-border bg-muted text-label-sm font-semibold uppercase tracking-wider text-on-surface-variant">
                <th scope="col" className="py-2.5 pl-4 pr-3">Product</th>
                <th scope="col" className="py-2.5 pr-3">Packing</th>
                <th scope="col" className="py-2.5 pr-3 text-right">Ordered</th>
                <th scope="col" className="py-2.5 pr-3">Stock</th>
                <th scope="col" className="w-32 py-2.5 pr-3 text-right">Packed</th>
                <th scope="col" className="py-2.5 pr-4 text-right">Shipped</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {order.lines.map((l) => (
                <tr
                  key={l.index}
                  className={cn('hover:bg-muted/50', l.short > 0 && 'bg-destructive-subtle/60')}
                >
                  <td className="py-2.5 pl-4 pr-3">
                    <div className="font-semibold text-on-surface">{l.description}</div>
                    {/* What the customer asked for on THIS line. Italic and directly
                        under the name, matching how it prints on the documents. */}
                    {l.notes && (
                      <div className="text-body-xs italic text-on-surface-variant">{l.notes}</div>
                    )}
                    <div className="text-body-xs text-on-surface-variant">
                      {[l.productCode, l.grade && `Grade: ${l.grade}`].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-on-surface-variant">
                    {[l.packagingType, l.totalPackSize, l.unit].filter(Boolean).join(' · ') || '—'}
                    {/* Pieces per container ("20 pcs/pack") — the combined
                        pack size (l.totalPackSize, e.g. "900 ML x 20") already
                        says this, but not every line has that text, so the
                        raw count is shown too whenever it's known. */}
                    {l.qtyPerPack > 0 && (
                      <div className="text-body-xs text-on-surface-subtle"><span className="font-mono tabular-nums">{l.qtyPerPack}</span> pcs/pack</div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono tabular-nums text-on-surface">{l.required}</td>
                  <td className="py-2.5 pr-3"><StockCell line={l} /></td>
                  <td className="py-2.5 pr-3">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={packed[l.index] ?? ''}
                      onChange={(e) => setPacked((p) => ({ ...p, [l.index]: e.target.value }))}
                      placeholder="—"
                      aria-label={`Packed quantity for ${l.description}`}
                      className="w-full rounded-lg border border-input bg-surface px-2 py-1.5 text-right font-mono tabular-nums text-data-md text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </td>
                  <td className="py-2.5 pr-4 text-right font-mono tabular-nums text-on-surface-variant">
                    {l.shippedQuantity || '—'}
                  </td>
                </tr>
              ))}
              {order.lines.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-body-sm text-on-surface-variant">
                    This order carries no product lines, so there is nothing to pick. Raise it again from the confirmed quotation.
                  </td>
                </tr>
              )}
            </tbody>
            {/* Printed-invoice totals convention: hairline above, label in caps,
                every figure mono and right-aligned under its column. */}
            {order.lines.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted/60">
                  <td className="py-2.5 pl-4 pr-3 text-label-sm uppercase tracking-wider text-on-surface-variant" colSpan={2}>
                    Total
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono tabular-nums font-semibold text-on-surface">{totals.ordered}</td>
                  <td className="py-2.5 pr-3 text-label-sm uppercase tracking-wider text-on-surface-subtle">Units</td>
                  <td className="py-2.5 pr-3 text-right font-mono tabular-nums font-semibold text-on-surface">{totals.packedQty}</td>
                  <td className="py-2.5 pr-4 text-right font-mono tabular-nums font-semibold text-on-surface">{order.lines.reduce((s, l) => s + (Number(l.shippedQuantity) || 0), 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {order.specialInstructions && (
          <div className="border-t border-border px-4 py-3 text-body-sm text-on-surface-variant">
            <span className="inline-flex items-center gap-1 font-semibold text-on-surface">
              <FileText className="h-3 w-3" aria-hidden /> Instructions:
            </span>{' '}
            {order.specialInstructions}
          </div>
        )}
      </div>

      <p className="text-body-sm text-on-surface-subtle">
        Packed quantities are Dispatch&apos;s own record. Stock moves when the order is fulfilled, which ships
        what was packed — or the full ordered quantity if nothing was recorded here.
      </p>
    </div>
  );
}

/** Labelled value inside the hand-off grid — the label is what the operator
    scans for, the value is what they act on. */
function Field({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2 lg:col-span-3')}>
      <p className="text-label-sm uppercase tracking-wider text-on-surface-subtle">{label}</p>
      <div className="mt-0.5 break-words text-body-md text-on-surface">{children}</div>
    </div>
  );
}

/** What is NOT yet on the order, marked the same way everywhere on this sheet. */
function MissingChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-card-sm border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-body-xs font-semibold text-warning-text">
      {children}
    </span>
  );
}

function InlineAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-2 text-body-sm font-semibold text-primary underline-offset-2 hover:text-primary-dim hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </button>
  );
}

export default DispatchOrderDetailPage;
